import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../auth/presentation/hooks/useAuth";
import { CompanyListPage } from "./CompanyListPage";
import { UserListPage } from "./UserListPage";
import { ProvisioningPage } from "./ProvisioningPage";
import { CronsPage } from "./CronsPage";
import { DatabaseMigrationsPage } from "./DatabaseMigrationsPage";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Tabs } from "../../../../ui/components/Tabs";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  FeatherLogOut,
  FeatherUsers,
  FeatherBuilding,
  FeatherBarChart3,
  FeatherFileText,
  FeatherServer,
  FeatherClock,
  FeatherDatabase,
  FeatherUserPlus,
  FeatherBot,
  FeatherArrowRight,
} from "@subframe/core";
import { ComplianceCalendarPage } from "../../../compliance/presentation/pages/master/ComplianceCalendarPage";
import { ZohoBotPage } from "../../../zoho-bot/presentation/pages/ZohoBotPage";

interface AdminDashboardProps {
  onLogout: () => void;
}

type AdminSection =
  | "overview"
  | "provisioning"
  | "companies"
  | "users"
  | "compliance"
  | "crons"
  | "database-migrations"
  | "zoho-bot";

const VALID_SECTIONS: AdminSection[] = [
  "overview",
  "provisioning",
  "companies",
  "users",
  "compliance",
  "crons",
  "database-migrations",
  "zoho-bot",
];

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getInitialSection = (): AdminSection => {
    const tab = searchParams.get("tab");
    if (tab && VALID_SECTIONS.includes(tab as AdminSection)) {
      return tab as AdminSection;
    }
    return "overview";
  };

  const [activeSection, setActiveSection] = useState<AdminSection>(
    getInitialSection()
  );

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && VALID_SECTIONS.includes(tab as AdminSection)) {
      const newSection = tab as AdminSection;
      setActiveSection((prevSection) => {
        return newSection !== prevSection ? newSection : prevSection;
      });
    }
  }, [searchParams]);

  const handleTabClick = (section: AdminSection): void => {
    setActiveSection(section);
    const currentPath = window.location.pathname;
    navigate(`${currentPath}?tab=${section}`, { replace: true });
  };

  const quickActions: {
    title: string;
    description: string;
    icon: React.ReactNode;
    onClick: () => void;
  }[] = [
    {
      title: "Client Onboarding",
      description: "Start a client, or resume one part-way through",
      icon: <FeatherUserPlus className="h-5 w-5 text-brand-600" />,
      onClick: () => window.open("/admin/client-onboarding", "_blank"),
    },
    {
      title: "Provisioning",
      description: "Prepare DB + S3 before adding a company",
      icon: <FeatherServer className="h-5 w-5 text-brand-600" />,
      onClick: () => handleTabClick("provisioning"),
    },
    {
      title: "Companies",
      description: "Manage tenant companies",
      icon: <FeatherBuilding className="h-5 w-5 text-brand-600" />,
      onClick: () => handleTabClick("companies"),
    },
    {
      title: "Users",
      description: "Manage all users",
      icon: <FeatherUsers className="h-5 w-5 text-brand-600" />,
      onClick: () => handleTabClick("users"),
    },
    {
      title: "Compliance",
      description: "Manage compliance types and calendar",
      icon: <FeatherFileText className="h-5 w-5 text-brand-600" />,
      onClick: () => handleTabClick("compliance"),
    },
  ];

  const renderSection = (): React.ReactNode => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="flex w-full flex-col items-start gap-4 p-8">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Quick actions
            </span>
            <div className="grid w-full gap-4 md:grid-cols-2 xl:grid-cols-3">
              {quickActions.map((action) => (
                <button
                  key={action.title}
                  type="button"
                  onClick={action.onClick}
                  className="group flex flex-col items-start gap-3 rounded-lg border border-solid border-neutral-border bg-default-background p-5 text-left transition-all hover:border-neutral-400 hover:shadow-sm"
                >
                  <div className="flex w-full items-center justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100">
                      {action.icon}
                    </div>
                    <FeatherArrowRight className="h-4 w-4 text-subtext-color opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      {action.title}
                    </span>
                    <span className="text-body font-body text-subtext-color">
                      {action.description}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case "provisioning":
        return <ProvisioningPage />;

      case "companies":
        return <CompanyListPage />;

      case "users":
        return <UserListPage />;

      case "compliance":
        return <ComplianceCalendarPage />;

      case "crons":
        return <CronsPage />;

      case "database-migrations":
        return <DatabaseMigrationsPage />;

      case "zoho-bot":
        return <ZohoBotPage />;

      default:
        return null;
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Header Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar size="large" variant="brand">
                {user?.userName?.[0]?.toUpperCase() || user?.email[0]?.toUpperCase() || "A"}
              </Avatar>
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Master Admin Dashboard
                  </span>
                  <Badge variant="brand">Master Admin</Badge>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Welcome back, {user?.email}
                </span>
              </div>
            </div>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherLogOut />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </div>

          {/* Navigation Tabs */}
          <Tabs>
            <Tabs.Item
              active={activeSection === "overview"}
              onClick={() => handleTabClick("overview")}
            >
              <FeatherBarChart3 className="h-4 w-4" />
              Overview
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "provisioning"}
              onClick={() => handleTabClick("provisioning")}
            >
              <FeatherServer className="h-4 w-4" />
              Provisioning
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "companies"}
              onClick={() => handleTabClick("companies")}
            >
              <FeatherBuilding className="h-4 w-4" />
              Companies
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "users"}
              onClick={() => handleTabClick("users")}
            >
              <FeatherUsers className="h-4 w-4" />
              Users
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "compliance"}
              onClick={() => handleTabClick("compliance")}
            >
              <FeatherFileText className="h-4 w-4" />
              Compliance
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "crons"}
              onClick={() => handleTabClick("crons")}
            >
              <FeatherClock className="h-4 w-4" />
              Crons
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "database-migrations"}
              onClick={() => handleTabClick("database-migrations")}
            >
              <FeatherDatabase className="h-4 w-4" />
              Database migrations
            </Tabs.Item>
            <Tabs.Item
              active={activeSection === "zoho-bot"}
              onClick={() => handleTabClick("zoho-bot")}
            >
              <FeatherBot className="h-4 w-4" />
              Zoho Books Automation Accounts
            </Tabs.Item>
          </Tabs>
        </div>

        {/* Dynamic Section Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
          {renderSection()}
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default AdminDashboard;

